import { Eye, Package, Pencil, Send, Trash2 } from "lucide-react"
import EstimationDownloadActions from "./EstimationDownloadActions.tsx"
import { isMaterialOutOfCatalog, type SelectedMaterial, type UserData } from "../../../../types/calculadoraDeMateriales.ts"
import styles from "../CalculadoraDeMateriales.module.scss"

type CalculadoraSummaryColumnProps = {
  groupedMaterials: Record<string, SelectedMaterial[]>
  selectedMaterials: SelectedMaterial[]
  isEstimacionDeCostos: boolean
  lastAddedMaterialId: number | string | null
  unitToShow: (unit: string) => string
  updateQuantity: (materialId: number | string, quantity: number) => void
  removeMaterial: (materialId: number | string) => void
  onEditMaterial?: (material: SelectedMaterial) => void
  handlePreviewButtonClick: () => void
  formattedTotal: string
  userData: UserData | null
  setShowSendQuotePopup: (v: boolean) => void
  handleSendQuoteClick: () => void
  hasChanges: () => boolean
  isEditMode: boolean
  showTransferToEstimation?: boolean
  onTransferToEstimation?: () => void
  estimationCatalogGapCount?: number
  onRequestQuotesFromEstimation?: () => void
}

const CalculadoraSummaryColumn = ({
  groupedMaterials,
  selectedMaterials,
  isEstimacionDeCostos,
  lastAddedMaterialId,
  unitToShow,
  updateQuantity,
  removeMaterial,
  onEditMaterial,
  handlePreviewButtonClick,
  formattedTotal,
  userData,
  setShowSendQuotePopup,
  handleSendQuoteClick,
  hasChanges,
  isEditMode,
  showTransferToEstimation = false,
  onTransferToEstimation,
  estimationCatalogGapCount = 0,
  onRequestQuotesFromEstimation,
}: CalculadoraSummaryColumnProps) => {
  return (
    <div className={styles.container_summaryColumn}>
      <div className={styles.container_summary}>
        <div className={styles.container_summary_header}>
          <h2 className={styles.container_summary_title}>Resumen</h2>
          <button
            className={styles.container_summary_showPreviewBtn}
            onClick={handlePreviewButtonClick}
            title="Vista previa de materiales"
          >
            <Eye size={20} style={{ minWidth: "2rem", minHeight: "2rem" }} />
          </button>
        </div>
        {selectedMaterials && selectedMaterials.length > 0 ? (
          <div className={styles.container_summary_info}>
            {Object.entries(groupedMaterials)
              .sort(([a], [b]) => a.localeCompare(b, "es"))
              .map(([category, materials]) => {
                const categoryTotalQuantity = materials.reduce(
                  (sum, item) => sum + (Number(item.quantity) || 0),
                  0
                )
                const categoryTotalPrice = isEstimacionDeCostos
                  ? materials.reduce(
                      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
                      0
                    )
                  : null

                return (
                  <div key={category} className={styles.container_summary_info_category}>
                    <div className={styles.container_summary_info_category_header}>
                      <div className={styles.container_summary_info_category_heading}>
                        <h3 className={styles.container_summary_info_category_title}>
                          {category || "Sin categoría"}
                        </h3>
                        <div className={styles.container_summary_info_category_subtitle}>
                          <span>
                            {categoryTotalQuantity} {categoryTotalQuantity === 1 ? "material" : "materiales"}
                          </span>
                          {categoryTotalPrice !== null && (
                            <span>
                              {new Intl.NumberFormat("es-AR", {
                                style: "currency",
                                currency: "ARS",
                              }).format(categoryTotalPrice)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.container_summary_info_category_items}>
                      {materials.map((material) => {
                        const subtotal =
                          isEstimacionDeCostos && material.price != null
                            ? (Number(material.price) || 0) * (Number(material.quantity) || 0)
                            : null
                        const rawDifferent =
                          Boolean(material.rawName?.trim()) &&
                          material.rawName!.trim() !== String(material.name).trim()
                        return (
                          <div
                            key={material.id}
                            data-summary-material-id={material.id}
                            className={`${styles.container_summary_info_category_item} ${lastAddedMaterialId === material.id ? styles.container_summary_info_category_item_highlighted : ""}`}
                          >
                            <div className={styles.container_summary_info_category_item_header}>
                              <span className={styles.container_summary_info_category_item_name}>{material.name}</span>
                              <span className={styles.container_summary_info_category_item_badge}>
                                {unitToShow(material.unit)}
                              </span>
                            </div>
                            {rawDifferent ? (
                              <p className={styles.container_summary_info_category_item_rawLine}>
                                En el archivo: {material.rawName}
                              </p>
                            ) : null}
                            {isEstimacionDeCostos && isMaterialOutOfCatalog(material) ? (
                              <p className={styles.container_summary_info_category_item_estimationNote}>
                                Lo detectamos en el listado; no hay precio de referencia en el catálogo Edify. Podés
                                publicar un pedido para recibir ofertas de proveedores.
                              </p>
                            ) : null}
                            {isEstimacionDeCostos &&
                            !isMaterialOutOfCatalog(material) &&
                            material.price == null ? (
                              <p className={styles.container_summary_info_category_item_estimationNote}>
                                Material en catálogo sin precio de referencia numérico.
                              </p>
                            ) : null}
                            {isEstimacionDeCostos && material.catalogMaterialUnquoted ? (
                              <p className={styles.container_summary_info_category_item_estimationNoteMuted}>
                                Precio referencia; puede figurar a consultar en listados del catálogo.
                              </p>
                            ) : null}
                            {subtotal !== null && (
                              <div className={styles.container_summary_info_category_item_price}>
                                {new Intl.NumberFormat("es-AR", {
                                  style: "currency",
                                  currency: "ARS",
                                }).format(subtotal)}
                              </div>
                            )}
                            <div className={styles.container_summary_info_category_item_actions}>
                              <div className={styles.container_summary_info_category_item_quantity}>
                                <span className={styles.container_summary_info_category_item_quantityLabel}>
                                  Cantidad
                                </span>
                                <input
                                  type="number"
                                  min={1}
                                  className={styles.container_summary_info_category_quantityInput}
                                  value={material.quantity}
                                  onChange={(e) =>
                                    updateQuantity(
                                      material.id,
                                      Number.parseInt(e.target.value, 10) || material.quantity
                                    )
                                  }
                                />
                              </div>
                              <div className={styles.container_summary_info_category_actions_iconGroup}>
                                {onEditMaterial ? (
                                  <button
                                    type="button"
                                    onClick={() => onEditMaterial(material)}
                                    className={styles.container_summary_info_category_actions_editBtn}
                                    title="Editar material"
                                    aria-label="Editar material"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => removeMaterial(material.id)}
                                  className={styles.container_summary_info_category_actions_removeBtn}
                                  title="Quitar de la lista"
                                  aria-label="Quitar de la lista"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
          </div>
        ) : (
          <div className={styles.container_summary_empty}>
            <Package size={64} className={styles.container_summary_empty_icon} />
            <h2 className={styles.container_summary_empty_title}>No hay materiales seleccionados</h2>
            <p className={styles.container_summary_empty_text}>
              Agregá materiales desde el panel izquierdo para ver el resumen
            </p>
          </div>
        )}
        <div className={styles.container_summary_info_category_total}>
          {isEstimacionDeCostos ? (
            <>
              <div className={styles.container_summary_info_category_total_text}>
                Total estimado
                <span className={styles.container_summary_info_category_total_price}>{formattedTotal}</span>
              </div>
              <p className={styles.container_summary_info_category_total_disclaimer}>
                *Los precios son estimativos y pueden variar.
              </p>
            </>
          ) : (
            <div className={styles.container_summary_info_category_total_text}>
              <span className={styles.container_summary_info_category_total_materialsLabel}>Materiales en la lista</span>
              <span className={styles.container_summary_info_category_total_materialsCount}>
                {selectedMaterials.length}
              </span>
            </div>
          )}
          <div className={styles.container_summary_info_category_total_actions}>
            {isEstimacionDeCostos ? (
              <EstimationDownloadActions
                userData={userData}
                materials={selectedMaterials}
                setShowSendQuotePopup={setShowSendQuotePopup}
                className={`${styles.container_header_download} ${styles.container_summary_info_category_total_downloadBtn}`}
                closePreview={() => {}}
                previewPopUp={false}
              />
            ) : (
              <button
                className={styles.container_header_quoteBtn}
                onClick={handleSendQuoteClick}
                disabled={isEditMode ? !hasChanges() : selectedMaterials.length === 0}
              >
                <Send size={16} style={{ minHeight: "1.6rem", minWidth: "1.6rem" }} />
                <span>{isEditMode ? "Guardar cambios" : "Enviar para cotizar"}</span>
              </button>
            )}
          </div>
          {showTransferToEstimation && onTransferToEstimation ? (
            <div className={styles.container_summary_estimationTransfer}>
              <button
                type="button"
                className={styles.container_summary_estimationTransfer_link}
                onClick={onTransferToEstimation}
                title="Referencia con precios del catálogo. Para ofertas de proveedores usá 'Enviar para cotizar'."
              >
                Ver precios estimados de referencia (misma lista)
              </button>
            </div>
          ) : null}
          {isEstimacionDeCostos &&
          estimationCatalogGapCount > 0 &&
          onRequestQuotesFromEstimation ? (
            <div className={styles.container_summary_estimationPublishHint}>
              <button
                type="button"
                className={styles.container_summary_estimationPublishHintBtn}
                onClick={onRequestQuotesFromEstimation}
              >
                Ir a pedir cotización con esta lista
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default CalculadoraSummaryColumn
